import React, { useState, useRef } from 'react';
import { X, Download, Share2, Smile } from 'lucide-react';
import { Achievement, Badge } from '../../types';
import Button from '../common/Button';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import html2canvas from 'html2canvas';
import logo from '../../assets/logo.png';

interface ShareAchievementModalProps {
  achievement: Achievement | Badge;
  onClose: () => void;
}

const ShareAchievementModal: React.FC<ShareAchievementModalProps> = ({ achievement, onClose }) => {
  const [customText, setCustomText] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const handleShare = async () => {
    const element = printRef.current;
    if (element) {
      try {
        // Ensure images are visible for html2canvas
        const imgs = element.querySelectorAll('img');
        imgs.forEach(img => { 
          img.crossOrigin = 'anonymous'; 
        });

        const canvas = await html2canvas(element, { 
          useCORS: true, 
          backgroundColor: null,
          scale: 2, // Higher quality
          logging: false
        });
        
        const dataUrl = canvas.toDataURL('image/png');
        const blob = await fetch(dataUrl).then(res => res.blob());
        const file = new File([blob], `${achievement.name}-achievement.png`, { type: 'image/png' });

        // Prepare share text
        const shareText = `${achievement.name}\n${achievement.description}\n${customText ? customText + '\n' : ''}https://lingualearn.web.app`;

        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: `I unlocked the ${achievement.name} achievement on LinguaLearn!`,
            text: shareText,
            files: [file],
          });
        } else {
          // Fallback: download the image
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${achievement.name}-achievement.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error('Error sharing:', error);
        // Fallback to text sharing
        const shareText = `I unlocked the ${achievement.name} achievement on LinguaLearn! ${achievement.description} ${customText ? customText + ' ' : ''}https://lingualearn.web.app`;
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`, '_blank');
      }
    }
  };

  const handleDownload = async () => {
    const element = printRef.current;
    if (element) {
      try {
        const imgs = element.querySelectorAll('img');
        imgs.forEach(img => { 
          img.crossOrigin = 'anonymous'; 
        });

        const canvas = await html2canvas(element, { 
          useCORS: true, 
          backgroundColor: null,
          scale: 2
        });
        
        const dataUrl = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `${achievement.name}-achievement.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (error) {
        console.error('Error downloading:', error);
      }
    }
  };

  const onEmojiClick = (emojiObject: EmojiClickData) => {
    setCustomText(prevText => prevText + emojiObject.emoji);
    setShowEmojiPicker(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <h3 className="text-xl font-semibold text-gray-900">Share Achievement</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            aria-label="close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          {/* Preview of the generated image */}
          <div className="mb-6">
            <div 
              ref={printRef} 
              className="w-full max-w-md mx-auto bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 text-white text-center p-8 relative overflow-hidden"
            >
              {/* Background pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }} />
              </div>

              {/* Logo and website name */}
              <div className="flex items-center justify-center w-full mb-8 relative z-10">
                <img referrerPolicy="no-referrer" src={logo} alt="Logo" className="absolute left-0 w-24 h-24 top-1/2 -translate-y-1/2" crossOrigin="anonymous" />
                <div className="font-bold text-3xl">LinguaLearn</div>
              </div>

              {/* Medal Icon */}
              <div className="flex justify-center mb-6 relative z-10">
                <svg width="120" height="120" viewBox="0 0 100 100">
                  {/* Medal ribbon - white and purple */}
                  <path d="M20 0 L40 40 L60 40 L80 0" fill="#fff" />
                  <path d="M20 0 L40 40 L60 40 L80 0" fill="#8b5cf6" opacity="0.7" transform="translate(0,10)" />
                  {/* Medal body */}
                  <circle cx="50" cy="60" r="30" fill="gold" stroke="#fff" strokeWidth="4" />
                  <circle cx="50" cy="60" r="25" fill="#f3e8ff" opacity="0.3" />
                  {/* Shine effect */}
                  <ellipse cx="50" cy="50" rx="12" ry="5" fill="#fff" opacity="0.3" />
                  {/* Inner circle for achievement icon */}
                  <circle cx="50" cy="60" r="15" fill="#fff" opacity="0.95" />
                  {/* Trophy emoji in center */}
                  <text x="50" y="68" textAnchor="middle" fontSize="16">🏆</text>
                  {/* Additional shine effects */}
                  <ellipse cx="45" cy="45" rx="8" ry="3" fill="#fff" opacity="0.4" />
                  <ellipse cx="55" cy="55" rx="6" ry="2" fill="#fff" opacity="0.3" />
                </svg>
              </div>

              {/* Achievement content */}
              <div className="relative z-10">
                <h3 className="text-3xl font-bold mb-4">{achievement.name}</h3>
                <p className="text-lg mb-4 opacity-90">{achievement.description}</p>
                {customText && (
                  <div className="bg-white bg-opacity-20 rounded-lg p-3 mb-4">
                    <p className="text-xl font-semibold break-words leading-relaxed">
                      {customText.split(' ').map((word, index) => {
                        // If word is longer than 15 characters, break it
                        if (word.length > 15) {
                          const chunks = [];
                          for (let i = 0; i < word.length; i += 15) {
                            chunks.push(word.slice(i, i + 15));
                          }
                          return (
                            <span key={index}>
                              {chunks.map((chunk, chunkIndex) => (
                                <span key={chunkIndex}>
                                  {chunk}
                                  {chunkIndex < chunks.length - 1 && <br />}
                                </span>
                              ))}
                              {index < customText.split(' ').length - 1 && ' '}
                            </span>
                          );
                        }
                        return (
                          <span key={index}>
                            {word}
                            {index < customText.split(' ').length - 1 && ' '}
                          </span>
                        );
                      })}
                    </p>
                  </div>
                )}
                <div className="text-sm opacity-80">https://lingualearn.web.app</div>
              </div>
            </div>
          </div>

          {/* Custom text input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Add your message (optional)
            </label>
            <div className="relative">
              <textarea
                className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Share your thoughts about this achievement..."
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                rows={3}
                maxLength={100}
                style={{ wordBreak: 'break-word' }}
              />
              <button
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
              >
                <Smile className="h-5 w-5" />
              </button>
            </div>
            {showEmojiPicker && (
              <div className="absolute z-50 mt-2">
                <EmojiPicker onEmojiClick={onEmojiClick} />
              </div>
            )}
            <div className="text-xs text-gray-500 mt-1 text-right">
              {customText.length}/100
              {customText.split(' ').some(word => word.length > 15) && (
                <span className="ml-2 text-orange-500">
                  ⚠️ Long words will be wrapped
                </span>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleDownload}
              leftIcon={<Download className="h-4 w-4" />}
              fullWidth
            >
              Download
            </Button>
            <Button
              variant="primary"
              onClick={handleShare}
              leftIcon={<Share2 className="h-4 w-4" />}
              fullWidth
            >
              Share
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareAchievementModal;
